/**
 * sessionPolicyEnvelope — the Time & Attendance half of the account-default /
 * location-value settings: the cells of the work-session policy a location
 * may customize, their labels and formatters, and the geofence status copy.
 *
 * Wire contract (GET/PATCH/DELETE /accounts/{a}/locations/{l}/session-policy):
 * every flat field as before, plus `sources` (one per cell, and
 * `geofence_centre` when a centre exists), `overrides`, `account`, `place`,
 * `geofence_enforced` and `geofence_not_enforced_reason`. PATCH is tri-state
 * (absent = untouched, null = follow the account, value = set); turning
 * `enable_geofencing` off also pins both scoped cells false server-side.
 *
 * The fence CENTRE is not a policy cell: it comes from the location address
 * (geocoded) or a manual pin, and is read/written through /locations/{l}/geofence.
 */
import type { GeofenceNotEnforcedReason, GeofencePlace, GeofencePlaceStatus } from '@turn-connect/shared';
import { normalizeGeofencePlace } from '@turn-connect/shared';
import { countCustom, formatTimeOfDay, type PolicyPatch, type PolicySources } from './policyEnvelope';
import { formatRadius } from '../ui/geofenceRadius';

export const SESSION_POLICY_FAMILY = 'session_policy';
/** Server `area` filter for the deviations feed of this topic. */
export const TIME_ATTENDANCE_AREA = 'time-attendance';

export interface SessionPolicyFields {
  // Geofence rules
  enable_geofencing: boolean;
  enable_geofencing_timeclock: boolean;
  enable_geofencing_work_orders: boolean;
  /** Metres. */
  geofence_radius_meters: number;
  // Fraud rules
  enable_fraud_detection: boolean;
  enable_fraud_detection_timeclock: boolean;
  enable_fraud_detection_work_orders: boolean;
  require_consistent_device: boolean;
  require_consistent_device_timeclock: boolean;
  require_consistent_device_work_orders: boolean;
  flag_unusual_hours: boolean;
  flag_unusual_hours_timeclock: boolean;
  flag_unusual_hours_work_orders: boolean;
  /** HH:MM in the draft; HH:MM:SS on the wire. */
  unusual_hours_start: string;
  unusual_hours_end: string;
  // Auto logout
  enable_auto_checkout: boolean;
  /** HH:MM, location timezone. */
  auto_checkout_time: string;
}

export type SessionPolicyField = keyof SessionPolicyFields;

export const GEOFENCE_RULE_FIELDS: SessionPolicyField[] = [
  'enable_geofencing',
  'enable_geofencing_timeclock',
  'enable_geofencing_work_orders',
  'geofence_radius_meters',
];
export const FRAUD_RULE_FIELDS: SessionPolicyField[] = [
  'enable_fraud_detection',
  'enable_fraud_detection_timeclock',
  'enable_fraud_detection_work_orders',
  'require_consistent_device',
  'require_consistent_device_timeclock',
  'require_consistent_device_work_orders',
  'flag_unusual_hours',
  'flag_unusual_hours_timeclock',
  'flag_unusual_hours_work_orders',
  'unusual_hours_start',
  'unusual_hours_end',
];
export const AUTO_LOGOUT_FIELDS: SessionPolicyField[] = ['enable_auto_checkout', 'auto_checkout_time'];
export const SESSION_POLICY_FIELDS: SessionPolicyField[] = [...GEOFENCE_RULE_FIELDS, ...FRAUD_RULE_FIELDS, ...AUTO_LOGOUT_FIELDS];

export const SESSION_POLICY_SYSTEM_DEFAULTS: SessionPolicyFields = {
  enable_geofencing: false,
  enable_geofencing_timeclock: false,
  enable_geofencing_work_orders: false,
  geofence_radius_meters: 100,
  enable_fraud_detection: false,
  enable_fraud_detection_timeclock: false,
  enable_fraud_detection_work_orders: false,
  require_consistent_device: false,
  require_consistent_device_timeclock: false,
  require_consistent_device_work_orders: false,
  flag_unusual_hours: false,
  flag_unusual_hours_timeclock: false,
  flag_unusual_hours_work_orders: false,
  unusual_hours_start: '22:00',
  unusual_hours_end: '05:00',
  enable_auto_checkout: false,
  auto_checkout_time: '23:59',
};

export type SessionPolicySourceKey = SessionPolicyField | 'geofence_centre';

export interface SessionPolicyEnvelope extends SessionPolicyFields {
  id?: string;
  account_id?: string;
  location_id?: string | null;
  sources: PolicySources<SessionPolicySourceKey>;
  /** Cells the account has locked for every location (never null from the server). */
  locked: string[];
  overrides?: Partial<SessionPolicyFields> | null;
  account?: (Partial<SessionPolicyFields> & { locked_fields?: string[] }) | null;
  place?: GeofencePlace | null;
  geofence_enforced: boolean;
  geofence_not_enforced_reason: GeofenceNotEnforcedReason;
  geofence_address?: string;
  location_timezone?: string;
}

export type SessionPolicyPatch = PolicyPatch<SessionPolicyFields>;

/**
 * The centre-related part of a fresh envelope laid over the current one:
 * place, enforcement and the centre's source. The policy cells stay as they
 * are — a centre action (recalculate / pin / clear) changes nothing else on
 * the server, and the owner may hold unsaved edits over those cells.
 */
export function mergeGeofenceCentre(current: SessionPolicyEnvelope, fresh: SessionPolicyEnvelope): SessionPolicyEnvelope {
  const centreSource = fresh.sources?.geofence_centre;
  return {
    ...current,
    place: normalizeGeofencePlace(fresh.place),
    geofence_enforced: fresh.geofence_enforced,
    geofence_not_enforced_reason: fresh.geofence_not_enforced_reason,
    geofence_address: fresh.geofence_address,
    location_timezone: fresh.location_timezone ?? current.location_timezone,
    sources: centreSource === undefined ? current.sources : { ...current.sources, geofence_centre: centreSource },
  };
}

/**
 * "N custom" for the location tab badge: the policy CELLS the location owns.
 * The geofence centre is location-owned by nature (`geofence_centre` source),
 * not a departure from the account, so it does not count.
 */
export function customSessionCellCount(sources: PolicySources | null | undefined): number {
  if (!sources) return 0;
  const { geofence_centre: _centre, ...cells } = sources as PolicySources<string>;
  return countCustom(cells);
}

// ── Labels & formatting ─────────────────────────────────────────────────────

export const SESSION_FIELD_LABELS: Record<SessionPolicyField, string> = {
  enable_geofencing: 'Enable geofencing',
  enable_geofencing_timeclock: 'Block check-in and check-out outside the fence',
  enable_geofencing_work_orders: 'Block work-order creation outside the fence',
  geofence_radius_meters: 'Radius',
  enable_fraud_detection: 'Enable fraud detection',
  enable_fraud_detection_timeclock: 'Run fraud detection on check-ins',
  enable_fraud_detection_work_orders: 'Run fraud detection on work orders',
  require_consistent_device: 'Flag unrecognized devices',
  require_consistent_device_timeclock: 'Flag check-ins from an unrecognized device',
  require_consistent_device_work_orders: 'Flag work orders from an unrecognized device',
  flag_unusual_hours: 'Flag unusual hours',
  flag_unusual_hours_timeclock: 'Flag check-ins during unusual hours',
  flag_unusual_hours_work_orders: 'Flag work orders during unusual hours',
  unusual_hours_start: 'Unusual hours start',
  unusual_hours_end: 'Unusual hours end',
  enable_auto_checkout: 'Enable auto logout',
  auto_checkout_time: 'Auto logout time',
};

/**
 * The session cells that hold a time of day (HH:MM in the draft). Exported so
 * the deviations table and its "Set a value" dialog can recognise a time cell
 * by KEY rather than by sniffing the value.
 */
export const SESSION_TIME_FIELDS: readonly SessionPolicyField[] = ['unusual_hours_start', 'unusual_hours_end', 'auto_checkout_time'];
const TIME_FIELDS = SESSION_TIME_FIELDS;

/**
 * How a cell's value reads in "Account setting: …" and the impact preview.
 * Times print the way the time control beside them does (formatTimeOfDay).
 */
export function formatSessionValue<K extends SessionPolicyField>(field: K, value: SessionPolicyFields[K]): string {
  if (field === 'geofence_radius_meters') return formatRadius(Number(value));
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (TIME_FIELDS.includes(field)) return formatTimeOfDay(value as string);
  return String(value ?? '');
}

// ── Wire normalisation ──────────────────────────────────────────────────────

/** "22:00", "22:00:00" and "2000-01-01T22:00:00Z" all → "22:00"; anything else → undefined. */
export function toHHMM(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  const iso = value.match(/T(\d{2}:\d{2})/);
  if (iso) return iso[1];
  const plain = value.match(/^(\d{2}:\d{2})/);
  return plain ? plain[1] : undefined;
}

function normaliseRow(raw: Record<string, unknown> | null | undefined): Partial<SessionPolicyFields> | null {
  if (!raw) return null;
  const row: Partial<SessionPolicyFields> = {};
  for (const f of SESSION_POLICY_FIELDS) {
    const v = raw[f];
    if (v === undefined || v === null) continue;
    if (f === 'geofence_radius_meters') (row as any)[f] = Number(v);
    else if (TIME_FIELDS.includes(f)) (row as any)[f] = toHHMM(v) ?? SESSION_POLICY_SYSTEM_DEFAULTS[f];
    else (row as any)[f] = v;
  }
  return row;
}

/**
 * Coerce a raw envelope into the typed shape: the radius arrives as a decimal
 * string, times as HH:MM:SS, and any cell the server left out takes the
 * system default so every draft field is defined.
 */
export function normalizeSessionEnvelope(raw: Record<string, any>): SessionPolicyEnvelope {
  const flat = normaliseRow(raw) ?? {};
  return {
    ...raw,
    ...SESSION_POLICY_SYSTEM_DEFAULTS,
    ...flat,
    sources: raw.sources ?? {},
    locked: Array.isArray(raw.locked) ? raw.locked : [],
    overrides: normaliseRow(raw.overrides),
    account: raw.account ? { ...normaliseRow(raw.account), locked_fields: raw.account.locked_fields ?? undefined } : null,
    place: normalizeGeofencePlace(raw.place),
    geofence_enforced: !!raw.geofence_enforced,
    geofence_not_enforced_reason: (raw.geofence_not_enforced_reason ?? '') as GeofenceNotEnforcedReason,
  } as SessionPolicyEnvelope;
}

/** Draft values → wire values: the unusual-hours window goes out as HH:MM:SS. */
export function toWirePatch(patch: SessionPolicyPatch): SessionPolicyPatch {
  const out: SessionPolicyPatch = { ...patch };
  for (const f of ['unusual_hours_start', 'unusual_hours_end'] as const) {
    const v = out[f];
    if (typeof v === 'string' && /^\d{2}:\d{2}$/.test(v)) out[f] = `${v}:00`;
  }
  return out;
}

// ── Geofence centre copy ────────────────────────────────────────────────────

const ACCURACY_WORD: Record<string, string> = {
  ROOFTOP: 'Rooftop',
  RANGE_INTERPOLATED: 'Range',
  GEOMETRIC_CENTER: 'Centre of area',
  APPROXIMATE: 'Approximate',
};

const STATUS_WORD: Record<GeofencePlaceStatus, string> = {
  unset: 'no centre',
  pending: 'locating…',
  verified: 'verified',
  unverified: 'not verified',
  review: 'needs review',
  failed: 'not found',
};

/** "Rooftop · verified", "Approximate · not verified", "Pinned · verified", "Pinned · needs review". */
export function accuracyChipLabel(place: GeofencePlace | null | undefined): string | null {
  if (!place) return null;
  // A verified GEOMETRIC_CENTER is the middle of one building Google matched
  // exactly; unverified, it is the middle of a street, postcode or town.
  const accuracyWord =
    place.accuracy === 'GEOMETRIC_CENTER' && place.status === 'verified' ? 'Centre of building' : ACCURACY_WORD[place.accuracy ?? ''];
  const head = place.source === 'manual' ? 'Pinned' : (accuracyWord ?? 'Located');
  return `${head} · ${STATUS_WORD[place.status] ?? place.status}`;
}

const shortDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** "Location address, checked Sep 3, 2026" | "Pinned by hand". */
export function centreSourceLabel(place: GeofencePlace | null | undefined): string {
  if (!place) return '—';
  if (place.source === 'manual') return 'Pinned by hand';
  const when = shortDate(place.geocoded_at);
  return when ? `Location address, checked ${when}` : 'Location address';
}

export const NOT_ENFORCED_REASON_TEXT: Record<Exclude<GeofenceNotEnforcedReason, ''>, string> = {
  disabled: 'geofencing is off',
  no_centre: 'no centre yet — complete the street address on Details, or pin the centre on the map',
  centre_unverified: 'the address could not be located precisely',
  centre_failed: 'the address could not be found',
};

/** The Status line of the "Where the fence is" card. */
export function geofenceStatusText(
  env: Pick<SessionPolicyEnvelope, 'geofence_enforced' | 'geofence_not_enforced_reason' | 'place'>
): string {
  if (env.geofence_enforced) {
    return env.place?.status === 'review' ? 'Enforced — address changed since this pin; confirm or recalculate' : 'Enforced';
  }
  const reason = env.geofence_not_enforced_reason;
  const why = reason ? NOT_ENFORCED_REASON_TEXT[reason] : undefined;
  return why ? `Not enforced — ${why}` : 'Not enforced';
}
